// import { Navbar } from "../components/layout/Navbar";
// import { Footer } from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Check, Star, ArrowRight, Building2, Briefcase, FileText, Globe, Users, Clock, Shield, Award } from "lucide-react";
import { motion } from "framer-motion";
import heroImage from "../assets/generated_images/diverse_team_working_in_a_modern_bright_office.png";
import abstractBg from "../assets/generated_images/abstract_blue_geometric_business_background.png";

interface HomeProps {
  showNavbar?: boolean;
  showFooter?: boolean;
  onNavigate?: (page: string) => void;
}

export default function Home({ showNavbar = false, showFooter = false, onNavigate }: HomeProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      } as const
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      {/* {showNavbar && <Navbar onNavigate={onNavigate} />} */}

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-muted/30 overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-10">
            <img src={abstractBg} alt="Background" className="w-full h-full object-cover" />
          </div>
          
          <div className="container mx-auto px-4 md:px-6 py-16 md:py-24 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div 
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                className="space-y-8"
              >
                <motion.div variants={itemVariants} className="inline-flex items-center gap-2 bg-blue-100 text-primary px-4 py-1.5 rounded-full text-sm font-semibold">
                  <Star className="h-4 w-4 fill-secondary text-secondary" />
                  <span>Trusted by 500,000+ Businesses</span>
                </motion.div>
                
                <motion.h1 variants={itemVariants} className="text-5xl md:text-6xl lg:text-7xl font-bold font-heading leading-tight text-primary">
                  Start Your Business <br/>
                  <span className="text-secondary">With Confidence.</span>
                </motion.h1>
                
                <motion.p variants={itemVariants} className="text-lg md:text-xl text-muted-foreground max-w-xl leading-relaxed">
                  The smartest way to form your LLC, C-Corp, or Nonprofit. 
                  Fast, reliable, and backed by a 100% satisfaction guarantee.
                </motion.p>
                
                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-bold px-8 h-14 text-lg shadow-lg shadow-blue-500/20">
                    Get Started Now
                  </Button>
                  <Button size="lg" variant="outline" className="h-14 text-lg border-2 hover:bg-muted/50">
                    Talk to an Expert
                  </Button>
                </motion.div>

                <motion.div variants={itemVariants} className="flex items-center gap-6 text-sm font-medium text-muted-foreground pt-4">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>Free Business Consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-500" />
                    <span>No Hidden Fees</span>
                  </div>
                </motion.div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                  <img src={heroImage} alt="Team working" className="w-full object-cover aspect-[4/3] transform hover:scale-105 transition-transform duration-700" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-8 text-white">
                    <p className="font-bold text-lg">Founded by Attorneys</p>
                    <p className="text-sm opacity-90">Helping entrepreneurs since 1997</p>
                  </div>
                </div>
                {/* Floating Badge */}
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl flex items-center gap-4 animate-bounce hover:animate-none">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Award className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-primary text-lg">100%</p>
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Satisfaction Guarantee</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-primary text-white py-12">
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/10">
              <div className="space-y-2">
                <p className="text-4xl md:text-5xl font-bold font-heading">26+</p>
                <p className="text-blue-200 text-sm uppercase tracking-widest font-semibold">Years Experience</p>
              </div>
              <div className="space-y-2">
                <p className="text-4xl md:text-5xl font-bold font-heading">500k+</p>
                <p className="text-blue-200 text-sm uppercase tracking-widest font-semibold">Businesses Formed</p>
              </div>
              <div className="space-y-2">
                <p className="text-4xl md:text-5xl font-bold font-heading">50</p>
                <p className="text-blue-200 text-sm uppercase tracking-widest font-semibold">States Covered</p>
              </div>
              <div className="space-y-2">
                <p className="text-4xl md:text-5xl font-bold font-heading">4.9</p>
                <p className="text-blue-200 text-sm uppercase tracking-widest font-semibold">TrustPilot Score</p>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 font-heading text-primary">Choose Your Business Structure</h2>
              <p className="text-muted-foreground text-lg">We make it easy to form any type of business entity. Select the one that's right for you.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { 
                  title: "LLC (Limited Liability Company)", 
                  desc: "The most popular choice for small businesses. Protects personal assets while offering flexible management.",
                  icon: Shield,
                  color: "bg-blue-50 text-blue-600",
                  link: "/form-llc"
                },
                { 
                  title: "C Corporation", 
                  desc: "Best for businesses planning to raise capital or go public. Unlimited growth potential with formal structure.",
                  icon: Building2,
                  color: "bg-indigo-50 text-indigo-600"
                },
                { 
                  title: "S Corporation", 
                  desc: "Avoid double taxation. Profits pass through to owners' personal tax returns. Good for saving on taxes.",
                  icon: FileText,
                  color: "bg-teal-50 text-teal-600"
                },
                { 
                  title: "Nonprofit", 
                  desc: "Form an organization for a charitable cause. Get tax-exempt status and serve your community.",
                  icon: Users,
                  color: "bg-amber-50 text-amber-600"
                },
                { 
                  title: "Doing Business As (DBA)", 
                  desc: "Register a trade name without forming a new legal entity. Quickest way to operate under a business name.",
                  icon: Briefcase,
                  color: "bg-purple-50 text-purple-600"
                },
                { 
                  title: "Registered Agent", 
                  desc: "We can serve as your official point of contact for legal documents in any state.",
                  icon: Globe,
                  color: "bg-rose-50 text-rose-600"
                }
              ].map((service, idx) => (
                <Card key={idx} className="group hover:shadow-xl transition-all duration-300 border-muted hover:-translate-y-1">
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${service.color} transition-colors`}>
                      <service.icon className="h-7 w-7" />
                    </div>
                    <CardTitle className="text-xl font-heading text-primary">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base leading-relaxed">
                      {service.desc}
                    </CardDescription>
                  </CardContent>
                  <CardFooter>
                    <div>
                      <Button 
                        variant="ghost" 
                        className="p-0 text-secondary hover:bg-transparent hover:text-secondary/80 group-hover:translate-x-1 transition-transform cursor-pointer"
                        onClick={() => {
                          if (service.link === "/form-llc") {
                            onNavigate?.("company-formation");
                          }
                        }}
                      >
                        Learn More <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why CorpNet */}
        <section className="py-20 bg-muted/30 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-white/50 to-transparent pointer-events-none"></div>
          <div className="container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 space-y-8">
                <h2 className="text-3xl md:text-4xl font-bold font-heading text-primary">Why Entrepreneurs Trust CorpNet</h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-lg shadow-sm h-fit">
                      <Clock className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-primary">Fast & Efficient</h3>
                      <p className="text-muted-foreground mt-1">We offer rush filing options to get your business up and running in as little as 24 hours.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-lg shadow-sm h-fit">
                      <Users className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-primary">Real Experts</h3>
                      <p className="text-muted-foreground mt-1">We're not just software. Our team of U.S.-based experts is available to answer your questions.</p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="bg-white p-3 rounded-lg shadow-sm h-fit">
                      <Shield className="h-6 w-6 text-secondary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-primary">100% Satisfaction Guarantee</h3>
                      <p className="text-muted-foreground mt-1">If you're not happy with our services, we'll refund our service fees. No questions asked.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 border-b pb-4">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white font-bold text-lg">1</div>
                      <p className="font-medium text-lg">Select your business structure</p>
                    </div>
                    <div className="flex items-center gap-4 border-b pb-4">
                      <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">2</div>
                      <p className="font-medium text-lg">Fill out our simple online form</p>
                    </div>
                    <div className="flex items-center gap-4 border-b pb-4">
                      <div className="w-12 h-12 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-lg">3</div>
                      <p className="font-medium text-lg">We prepare and file your documents</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center font-bold text-lg">
                        <Check className="h-6 w-6" />
                      </div>
                      <p className="font-medium text-lg">You're officially in business!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-primary text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
             <img src={abstractBg} alt="" className="w-full h-full object-cover mix-blend-overlay" />
          </div>
          <div className="container mx-auto px-4 md:px-6 text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold font-heading mb-6">Ready to Make It Official?</h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Join 500,000+ entrepreneurs who started their journey with CorpNet.
              Get started today for as low as $79 + state fees.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white font-bold px-10 h-14 text-lg border-2 border-secondary">
                Launch My Business
              </Button>
              <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 h-14 text-lg">
                View Pricing
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* {showFooter && <Footer />} */}
    </div>
  );
}
